import classNames from "classnames/bind";
import * as React from "react";
import { Component } from "react";
import { useId } from "~/common/hooks/useId";
import { TipIcon } from "~/sharedComponents/tooltip/TipIcon";
import { SetValueActionCreator } from "../../Redux/Actions";
import * as styles from "./Switcher.less";

const ctx = classNames.bind(styles);

export interface SwitcherProps {
    /** Wrapper class */
    className?: string;
    isOn?: boolean;
    isDisabled?: boolean;
    onChangeAction?: SetValueActionCreator<boolean> | any; // remove any after connecting to redux store
    label?: string | React.ReactElement;
    labelAfterSwitcher?: boolean;
    isBig?: boolean;
    testId?: string;
    ["aria-describedby"]?: string;
    ["aria-labelledby"]?: string;
}

export class Switcher extends Component<SwitcherProps> {
    constructor(props: SwitcherProps) {
        super(props);
        this.onClick = this.onClick.bind(this);
    }

    onClick() {
        const { isOn, onChangeAction, isDisabled } = this.props;

        if (isDisabled) {
            return;
        }
        if (onChangeAction) {
            onChangeAction(!isOn);
        }
    }

    render() {
        const { isOn, label, labelAfterSwitcher, isBig, isDisabled, className, testId } = this.props;

        const trackStyles = ctx(styles.track, {
            on: isOn,
            left: !labelAfterSwitcher,
            right: labelAfterSwitcher,
            small: !isBig,
            big: isBig,
            disabled: isDisabled,
        });

        return (
            <div
                onClick={this.onClick}
                className={ctx(styles.switcher, className, {
                    [styles.isDisabled]: isDisabled,
                })}
                data-testid={testId ? `switcher-${testId}` : null}
                role="checkbox"
                aria-checked={isOn}
                aria-describedby={this.props["aria-describedby"]}
                aria-labelledby={this.props["aria-labelledby"]}
            >
                {label && !labelAfterSwitcher && getLabelElement(label, this.props["aria-labelledby"])}
                <div className={styles.track_wrapper}>
                    <div className={trackStyles}>
                        <div className={styles.button} />
                    </div>
                </div>
                {label && labelAfterSwitcher && getLabelElement(label, this.props["aria-labelledby"])}
            </div>
        );
    }
}

const getLabelElement = (label: SwitcherProps["label"], id?: string) => {
    return React.isValidElement(label) ? label : <span id={id}>{label}</span>;
};

/**
 * Renders switcher with label and description under it.
 */
export const SwitcherWithDescription: React.FC<SwitcherProps & { description: string }> = ({
    description,
    label,
    ...props
}) => {
    let descriptionId = useId();
    let labelId = useId();

    return (
        <Switcher
            {...props}
            aria-describedby={descriptionId}
            aria-labelledby={labelId}
            label={
                <div className={styles.switcher_label}>
                    <span id={labelId}>{label}</span>
                    {description && (
                        <span id={descriptionId} className={styles.description}>
                            {description}
                        </span>
                    )}
                </div>
            }
        />
    );
};

type SwitcherWithHintProps = SwitcherProps & {
    /**
     * Show more info when user hovers tip icon
     */
    hint: React.ReactNode;
    helpIcon?: React.ReactNode;
};

/**
 * Renders switcher with label and info icon.
 */
export const SwitcherWithHint: React.FC<SwitcherWithHintProps> = ({ label, hint, helpIcon, ...props }) => {
    let descriptionId = useId();

    return (
        <Switcher
            {...props}
            aria-describedby={descriptionId}
            label={
                <div className={ctx(styles.switcher_hint)}>
                    {label}
                    {helpIcon}
                    {hint && (
                        <span id={descriptionId} className={styles.description}>
                            <TipIcon>{hint}</TipIcon>
                        </span>
                    )}
                </div>
            }
        />
    );
};
